import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ActionIcon,
  Avatar,
  Badge,
  Box,
  Collapse,
  Group,
  Paper,
  Text,
  Tooltip,
} from "@mantine/core";
import {
  IconChevronDown,
  IconChevronRight,
  IconCircleCheck,
  IconClock,
} from "@tabler/icons-react";

const formatCurrency = value =>
  value == null
    ? "$0"
    : `$${Number(value).toLocaleString("en-US", {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      })}`;

function RecordNode({ record, depth }) {
  const [open, setOpen] = useState(true);
  const navigate = useNavigate();
  const children = Array.isArray(record.child_selling_deals)
    ? record.child_selling_deals
    : [];
  const hasChildren = children.length > 0;
  const isCompany = record.ps_deal_type === "COMPANY";
  const isMd = record.ps_deal_type === "MD";
  const recordUsers = Array.isArray(record.deal_users) ? record.deal_users : [];
  // Company nodes are grouping headers, not deals — only MD/Rep records open
  // the deal details page.
  const isDeal = !isCompany;

  return (
    <Box>
      <Paper
        withBorder
        p="sm"
        radius="md"
        mb="xs"
        ml={depth * 24}
        onClick={isDeal ? () => navigate(`/deals/${record.id}`) : undefined}
        style={isDeal ? { cursor: "pointer" } : undefined}
      >
        <Group justify="space-between" wrap="nowrap">
          <Group gap="sm" wrap="nowrap" style={{ flex: 1 }}>
            {hasChildren ? (
              <ActionIcon
                variant="subtle"
                color="gray"
                onClick={e => {
                  e.stopPropagation();
                  setOpen(o => !o);
                }}
                aria-label={open ? "Collapse" : "Expand"}
              >
                {open ? (
                  <IconChevronDown size={16} />
                ) : (
                  <IconChevronRight size={16} />
                )}
              </ActionIcon>
            ) : (
              <Box w={28} />
            )}

            {isCompany ? (
              <Text fw={600}>{record.company_name}</Text>
            ) : (
              <Group gap="lg" wrap="wrap">
                {recordUsers.map(u => (
                  <Group key={u.id} gap="xs" wrap="nowrap">
                    <Avatar src={u.profile_image_url} radius="xl" size="sm">
                      {(u.first_name || "?").charAt(0)}
                    </Avatar>
                    <Text size="sm">
                      {u.first_name} {u.last_name}
                    </Text>
                  </Group>
                ))}
              </Group>
            )}

            {!isCompany && (
              <Badge color={isMd ? "grape" : "blue"} variant="light">
                {isMd ? "MD" : "Rep"}
              </Badge>
            )}
          </Group>

          <Group gap="lg" wrap="nowrap">
            <Group gap="xs" wrap="nowrap">
              {record.is_active === true ? (
                <Tooltip label="Active">
                  <IconCircleCheck
                    size={18}
                    color="var(--mantine-color-green-6)"
                  />
                </Tooltip>
              ) : (
                <Tooltip label="Pending">
                  <IconClock size={18} color="var(--mantine-color-yellow-6)" />
                </Tooltip>
              )}
              <Text size="sm" c="dimmed">
                Accounts
              </Text>
              <Text fw={600}>{Number(record.current_accounts) || 0}</Text>
            </Group>
            <Group gap="xs" wrap="nowrap">
              <Text size="sm" c="dimmed">
                Revenue
              </Text>
              <Text fw={600}>{formatCurrency(record.current_revenue)}</Text>
            </Group>
          </Group>
        </Group>
      </Paper>

      {hasChildren && (
        <Collapse in={open}>
          {children.map(child => (
            <RecordNode key={child.id} record={child} depth={depth + 1} />
          ))}
        </Collapse>
      )}
    </Box>
  );
}

function DealHierarchy({ records }) {
  const list = Array.isArray(records) ? records : [];
  if (list.length === 0) {
    return (
      <Text size="sm" c="dimmed">
        No deals for this selling year.
      </Text>
    );
  }
  return (
    <Box>
      {list.map(record => (
        <RecordNode key={record.id} record={record} depth={0} />
      ))}
    </Box>
  );
}

export default DealHierarchy;
